import { CreateMultilegDialog, useCreateMultilegDialog } from '@src/components/MultilegCombo/CreateMultilegDialog';
import {
  SymbolSelector as BaseSymbolSelector,
  Button,
  ButtonVariants,
  FormControlSizes,
  HStack,
  IndicatorBadge,
  MixpanelEvent,
  ProductTypeEnum,
  SymbolSelectorAppearance,
  Text,
  getProductGroupLabel,
  useCallbackRef,
  useMixpanel,
  useSecuritiesContext,
  type AutocompleteGroup,
  type FuseSearchResult,
  type ProductGroup,
  type Security,
} from '@talos/kyoko';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useFeatureFlag } from '../../hooks';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import type { AvaSymbolSelectorProps } from './types';

/**
 * Wraps the SymbolSelector and applies the options we want within the Principal UI
 */
export const SymbolSelector = memo(function SymbolSelector({
  dropdownPlacement = 'bottom',
  dropdownWidth = '205px',
  maxHeight = 240,
  appearance = SymbolSelectorAppearance.Basic,
  showAddMultiLegButton = true,
  ...props
}: AvaSymbolSelectorProps) {
  const onSymbolChanged = useCallbackRef(props.onSymbolChanged);
  const closeMenuRef = useRef<(() => void) | null>(null);
  const mixpanel = useMixpanel();
  const createMultilegDialog = useCreateMultilegDialog({
    onUseSymbol: onSymbolChanged,
    useSymbolLabel: 'Use symbol',
  });
  const { enableCalendarSpreads, disableSymbolSelectorFuseSearch } = useFeatureFlag();
  const { searchableSecurities } = useSecuritiesContext();
  const { isPopout } = usePanelContext();

  const availableSecurities = useMemo(() => {
    const source = props.availableSecurities ?? searchableSecurities;
    return source.filter(security => {
      if (!props.availableSecurities && security.ProductType === ProductTypeEnum.OTC) {
        return false;
      }
      return enableCalendarSpreads ? true : security.ProductType !== ProductTypeEnum.CalendarSpread;
    });
  }, [enableCalendarSpreads, props.availableSecurities, searchableSecurities]);

  const { open: openCreateMultilegDialog } = createMultilegDialog;
  const handleAdd = useCallback(() => {
    openCreateMultilegDialog();
    closeMenuRef.current?.();
    mixpanel.track(MixpanelEvent.CreateNewMultileg);
  }, [openCreateMultilegDialog, mixpanel]);

  const OrderFormGroupHeader = useMemo(() => {
    return ({ group }: { group: AutocompleteGroup<FuseSearchResult<Security>> }) => {
      return (
        <HStack
          gap="spacing4"
          justifyContent="flex-start"
          background="backgroundDropdownGroupHeader"
          py="spacing4"
          px="spacing12"
          fontSize="fontSizeTiny"
          data-testid="dropdown-group-header"
        >
          <Text size="fontSizeTiny" textTransform="uppercase" style={{ fontWeight: 500 }}>
            {getProductGroupLabel(group.group as ProductGroup)}
          </Text>
          <IndicatorBadge children={group.items?.length} />
        </HStack>
      );
    };
  }, []);

  const suffixCb = useCallback(
    ({ selectedGroup }: { selectedGroup?: string }) => {
      if (!showAddMultiLegButton || !props.showGroups || selectedGroup !== 'Multi') {
        return props.suffix ?? null;
      }
      return (
        <HStack background="backgroundDropdownGroupHeader" py="spacing8" px="spacing12">
          <Button
            variant={ButtonVariants.Primary}
            onClick={handleAdd}
            width="100%"
            size={FormControlSizes.Small}
            data-testid="add-multileg"
          >
            + Create New Multileg Symbol
          </Button>
        </HStack>
      );
    },
    [showAddMultiLegButton, props.showGroups, props.suffix, handleAdd]
  );

  return (
    <>
      <BaseSymbolSelector
        dropdownPlacement={dropdownPlacement}
        dropdownWidth={dropdownWidth}
        maxHeight={maxHeight}
        RenderGroupHeader={OrderFormGroupHeader}
        appearance={appearance}
        closeMenuRef={closeMenuRef}
        suffixCb={suffixCb}
        {...props}
        onSymbolChanged={onSymbolChanged}
        availableSecurities={availableSecurities}
        closeOnInputBlur={!isPopout && !props.showGroups}
        disableFuseSearch={disableSymbolSelectorFuseSearch}
      />
      {createMultilegDialog.isOpen && <CreateMultilegDialog {...createMultilegDialog} />}
    </>
  );
});

export default SymbolSelector;
